import Modal from 'antd/lib/modal/Modal';
import Typography from '@mui/material/Typography';
import AntTypography from 'antd/lib/typography';
import Tag from 'antd/lib/tag';

import { IconAlertTriangle, IconCalendar, IconCloud, IconCloudRain, IconCloudStorm, IconMapPin, IconSnowflake, IconThermometer, IconWind } from '@tabler/icons-react';

import styles from './event-modal.module.css';

const { Text } = AntTypography;

export default function EventModal({ selectedEvent, showEventModal, setShowEventModal, duration }) {
    const getWeatherIcon = (weatherPrimary) => {
        const type = weatherPrimary?.toLowerCase() || '';
        if (type.includes('snow')) return <IconSnowflake size={24} />;
        if (type.includes('rain')) return <IconCloudRain size={24} />;
        if (type.includes('ice') || type.includes('freez')) return <IconThermometer size={24} style={{ transform: 'rotate(45deg)' }} />;
        if (type.includes('mix')) return <IconCloudStorm size={24} />;
        return <IconCloud size={24} />;
      };
    return (
        <Modal
        title={
          <div className={styles.modalConfirmHeader}>
            {getWeatherIcon(selectedEvent.weatherPrimary)}
            <Typography variant="h6">
              {selectedEvent.weatherPrimary || 'Weather Event'}
            </Typography>
          </div>
        }
        open={showEventModal}
        onCancel={() => setShowEventModal(false)}
        footer={null}
        width={900}
        className={styles.confirmModal}
        getContainer={false}
      >
        <div className={styles.confirmContent}>
          {selectedEvent.weather && (
            <div className={styles.threatAlert}>
              <IconAlertTriangle size={24} style={{ color: '#ff4d4f' }} />
              <Text style={{ color: '#ff4d4f', fontSize: '16px' }}>
                {selectedEvent.weather}
              </Text>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '24px' }}>
            <div>
              <div className={styles.threatModalSection}>
                <div className={styles.threatModalSectionHeader}>
                  <IconCalendar size={18} />
                  <Text strong>Time Period</Text>
                </div>
                <div className={styles.threatModalSectionContent}>
                  <div className={styles.threatTimeDetail}>
                    <Text type="secondary">Start:</Text>
                    <Text strong>{selectedEvent.formattedStartDate}</Text>
                  </div>
                  <div className={styles.threatTimeDetail}>
                    <Text type="secondary">End:</Text>
                    <Text strong>{selectedEvent.formattedEndDate}</Text>
                  </div>
                  <div className={styles.threatTimeDetail}>
                    <Text type="secondary">Duration:</Text>
                    <Text strong>{Math.round(duration * 10) / 10} hours</Text>
                  </div>
                </div>
              </div>

              <div className={styles.threatModalSection} style={{ marginTop: '16px' }}>
                <div className={styles.threatModalSectionHeader}>
                  <IconMapPin size={18} />
                  <Text strong>Location</Text>
                </div>
                <div className={styles.threatModalSectionContent}>
                  <Text>{selectedEvent.siteAddress}</Text>
                </div>
              </div>
            </div>

            <div>
              <div className={styles.threatModalSection}>
                <div className={styles.threatModalSectionHeader}>
                  <IconThermometer size={18} />
                  <Text strong>Weather Details</Text>
                </div>
                <div className={styles.threatModalSectionContent}>
                  {selectedEvent.maxTempF && selectedEvent.minTempF && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Temperature:</Text>
                      <Text strong>{selectedEvent.minTempC}°C - {selectedEvent.maxTempC}°C</Text>
                    </div>
                  )}
                  {selectedEvent.totalSnowCM > 0 && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Snow Accumulation:</Text>
                      <Text strong>{selectedEvent.totalSnowCM.toFixed(1)} cm</Text>
                    </div>
                  )}
                  {selectedEvent.snowTrace && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Snow:</Text>
                      <Tag>Trace Amounts</Tag>
                    </div>
                  )}
                  {selectedEvent.totalIceaccumMM > 0 && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Ice Accumulation:</Text>
                      <Text strong>{selectedEvent.totalIceaccumMM.toFixed(1)} mm</Text>
                    </div>
                  )}
                  {selectedEvent.iceCoating && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Ice:</Text>
                      <Tag>Ice Coating</Tag>
                    </div>
                  )}
                  {selectedEvent.iceTrace && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Ice:</Text>
                      <Tag>Trace Amounts</Tag>
                    </div>
                  )}
                  {selectedEvent.maxPop > 0 && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Probability:</Text>
                      <Text strong>{selectedEvent.maxPop}%</Text>
                    </div>
                  )}
                  {selectedEvent.hasGap && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Pattern:</Text>
                      <Tag color="warning">Intermittent</Tag>
                    </div>
                  )}
                  {selectedEvent.longDuration && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Duration Type:</Text>
                      <Tag color="warning">Extended Event</Tag>
                    </div>
                  )}
                  {selectedEvent.sustainedSnowfallPeriods?.length > 0 && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Snowfall Pattern:</Text>
                      <Tag color="processing">Sustained Periods</Tag>
                    </div>
                  )}
                  {selectedEvent.minDuration !== selectedEvent.maxDuration && selectedEvent.minDuration && selectedEvent.maxDuration && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Duration Range:</Text>
                      <Text strong>{selectedEvent.minDuration}-{selectedEvent.maxDuration} hours</Text>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.threatModalSection} style={{ marginTop: '16px' }}>
                <div className={styles.threatModalSectionHeader}>
                  <IconWind size={18} />
                  <Text strong>Conditions</Text>
                </div>
                <div className={styles.threatModalSectionContent}>
                  {selectedEvent.blowingSnowIntensity && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Blowing Snow:</Text>
                      <Tag color={selectedEvent.blowingSnowIntensity === 'h' ? 'error' : 'warning'}>
                        {selectedEvent.blowingSnowIntensity === 'h' ? 'High' : 'Moderate'}
                      </Tag>
                    </div>
                  )}
                  {selectedEvent.windChillIntensity && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Wind Chill:</Text>
                      <Tag color={selectedEvent.windChillIntensity === 'h' ? 'error' : 'warning'}>
                        {selectedEvent.windChillIntensity === 'h' ? 'High' : 'Moderate'}
                      </Tag>
                    </div>
                  )}
                  {selectedEvent.rapidSnowAccumulationIntensity && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Snow Rate:</Text>
                      <Tag color={selectedEvent.rapidSnowAccumulationIntensity === 'h' ? 'error' : 'warning'}>
                        Rapid Accumulation
                      </Tag>
                    </div>
                  )}
                  {selectedEvent.heavySleetIntensity && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Sleet:</Text>
                      <Tag color={selectedEvent.heavySleetIntensity === 'h' ? 'error' : 'warning'}>
                        {selectedEvent.heavySleetIntensity === 'h' ? 'Heavy' : 'Moderate'}
                      </Tag>
                    </div>
                  )}
                  {selectedEvent.hardFreezePotentialIntensity && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Hard Freeze Risk:</Text>
                      <Tag color={selectedEvent.hardFreezePotentialIntensity === 'h' ? 'error' : 'warning'}>
                        {selectedEvent.hardFreezePotentialIntensity === 'h' ? 'High' : 'Moderate'}
                      </Tag>
                    </div>
                  )}
                  {selectedEvent.heavySleetCount > 0 && (
                    <div className={styles.threatDetail}>
                      <Text type="secondary">Sleet Periods:</Text>
                      <Text strong>{selectedEvent.heavySleetCount}</Text>
                    </div>
                  )}
                </div>
              </div>

              {selectedEvent.threats?.length > 0 && (
                <div className={styles.threatModalSection} style={{ marginTop: '16px' }}>
                  <div className={styles.threatModalSectionHeader}>
                    <IconAlertTriangle size={18} />
                    <Text strong>Additional Threats</Text>
                  </div>
                  <div className={styles.threatModalSectionContent}>
                    {selectedEvent.threats.map((threat, index) => (
                      <div key={index} className={styles.threatDetail}>
                        <Text type="secondary">{threat.type}:</Text>
                        <Tag color="error">{threat.intensity}</Tag>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    )
}